import CloseIcon from '@mui/icons-material/Close'
import { Box, FormControlLabel, IconButton, Modal, RadioGroup, TextField, Typography } from '@mui/material'
import InputRadio from 'component/Input/InputRadio'
import InputSecondNewDatePicker from 'component/Input/InputSecondNewDatePicker'
import { colors } from 'constants/theme'
import moment from 'moment'
import MainButton from 'new-components/buttons/main-button'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { BranchVisitStatusValue } from 'utils/branchVisitStatus.utils'

export interface BranchVisitDialogResult {
  status: BranchVisitStatusValue
  firstVisitDate: string | null
  note: string
}

interface BranchVisitStatusDialogProps {
  isOpen: boolean
  branchCode: string
  loading?: boolean
  /** 'ambiguous' = คนไข้มี visit สาขาอื่น แต่มาสาขานี้ครั้งแรก (auto-prompt) | 'new-registration' = operator กด "+เพิ่มคนไข้ใหม่" */
  mode?: 'ambiguous' | 'new-registration'
  initialStatus?: BranchVisitStatusValue | null
  initialFirstVisitDate?: string | Date | null
  initialNote?: string | null
  onSkip: () => void
  onConfirm: (result: BranchVisitDialogResult) => void
}

const NOTE_MAX = 500

export default function BranchVisitStatusDialog(props: BranchVisitStatusDialogProps) {
  const { t } = useTranslation()
  const [choice, setChoice] = useState<BranchVisitStatusValue | ''>('')
  const [firstVisitDate, setFirstVisitDate] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [dateError, setDateError] = useState<string>('')

  const minDate = useMemo(() => moment().subtract(20, 'years').format('YYYY-MM-DD'), [])
  const maxDate = useMemo(() => moment().format('YYYY-MM-DD'), [])

  useEffect(() => {
    if (props.isOpen) {
      setChoice(props.initialStatus || '')
      setFirstVisitDate(
        props.initialFirstVisitDate
          ? moment(props.initialFirstVisitDate).format('YYYY-MM-DD')
          : ''
      )
      setNote(props.initialNote || '')
      setDateError('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen])

  const validateDate = (value: string): string => {
    if (!value) return ''
    const d = moment(value, 'YYYY-MM-DD', true)
    if (!d.isValid()) return t('REGISTER.SAVE_DIALOG.ERROR_INVALID_DATE')
    if (d.isAfter(moment(), 'day')) return t('REGISTER.SAVE_DIALOG.ERROR_FUTURE_DATE')
    if (d.isBefore(moment().subtract(20, 'years'), 'day')) return t('REGISTER.SAVE_DIALOG.ERROR_TOO_OLD')
    return ''
  }

  const handleDateChange = (value: string) => {
    setFirstVisitDate(value)
    setDateError(validateDate(value))
  }

  const handleConfirm = () => {
    if (!choice) return
    if (choice === 'EXISTING') {
      const err = validateDate(firstVisitDate)
      if (err) {
        setDateError(err)
        return
      }
      props.onConfirm({
        status: 'EXISTING',
        firstVisitDate: firstVisitDate || null,
        note: note.trim()
      })
    } else {
      props.onConfirm({
        status: 'NEW',
        firstVisitDate: null,
        note: ''
      })
    }
  }

  const submitDisabled = !choice || (choice === 'EXISTING' && !!dateError) || !!props.loading

  return (
    <>
      <Modal
        open={props.isOpen}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return
          props.onSkip()
        }}
        sx={{ overflow: 'auto' }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(560px, 95vw)',
            maxHeight: '90vh',
            overflowY: 'auto',
            backgroundColor: colors.white,
            borderRadius: '12px',
            boxShadow: 24,
            padding: '28px 32px 24px',
            outline: 'none'
          }}
        >
          <IconButton
            aria-label="close"
            onClick={props.onSkip}
            disabled={!!props.loading}
            sx={{ position: 'absolute', top: 12, right: 12, color: colors.disabledGray }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            sx={{
              color: colors.themeMainColor,
              fontSize: '20px',
              fontWeight: 600,
              textAlign: 'center',
              paddingX: '32px',
              lineHeight: 1.4,
              marginBottom: '8px',
              wordBreak: 'keep-all'
            }}
          >
            {t('REGISTER.VISIT_DIALOG.TITLE')}
          </Typography>
          <Typography
            sx={{
              color: colors.disabledGray,
              fontSize: '14px',
              textAlign: 'center',
              marginBottom: '20px'
            }}
          >
            {props.mode === 'new-registration'
              ? t('REGISTER.VISIT_DIALOG.DESCRIPTION_NEW')
              : t('REGISTER.VISIT_DIALOG.DESCRIPTION')}
          </Typography>

          <RadioGroup
            name="branch-visit-status"
            value={choice}
            onChange={(e) => setChoice(e.target.value as BranchVisitStatusValue)}
            sx={{ gap: '8px' }}
          >
            <FormControlLabel
              value="NEW"
              control={<InputRadio classes={{ root: 'custom-radio' }} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: '15px', color: colors.textPrimary, fontWeight: 600 }}>
                    {t('REGISTER.VISIT_DIALOG.OPTION_NEW', { branch: props.branchCode || '' })}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: colors.disabledGray }}>
                    {t('REGISTER.VISIT_DIALOG.OPTION_NEW_HINT')}
                  </Typography>
                </Box>
              }
              sx={{ marginLeft: 0, marginRight: 0, alignItems: 'flex-start', paddingTop: '4px' }}
            />
            <FormControlLabel
              value="EXISTING"
              control={<InputRadio classes={{ root: 'custom-radio' }} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: '15px', color: colors.textPrimary, fontWeight: 600 }}>
                    {t('REGISTER.VISIT_DIALOG.OPTION_EXISTING', { branch: props.branchCode || '' })}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: colors.disabledGray }}>
                    {t('REGISTER.VISIT_DIALOG.OPTION_EXISTING_HINT')}
                  </Typography>
                </Box>
              }
              sx={{ marginLeft: 0, marginRight: 0, alignItems: 'flex-start', paddingTop: '4px' }}
            />
          </RadioGroup>

          {/* Mini-form: ปรากฏเฉพาะตอนเลือก EXISTING */}
          {choice === 'EXISTING' && (
            <Box
              sx={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${colors.lightGray}`,
                display: 'flex',
                flexDirection: 'column',
                gap: '14px'
              }}
            >
              <Box>
                <Typography sx={{ fontSize: '13px', color: colors.textPrimary, marginBottom: '6px' }}>
                  {t('REGISTER.VISIT_DIALOG.FIRST_VISIT_DATE')}
                </Typography>
                <InputSecondNewDatePicker
                  value={firstVisitDate}
                  onchange={(val: any) => {
                    // picker คืน 'YYYY/MM/DD' หรือ Date — convert เป็น 'YYYY-MM-DD' (ISO) เก็บใน state
                    if (!val) {
                      handleDateChange('')
                      return
                    }
                    const iso = typeof val === 'string'
                      ? val.replace(/\//g, '-')
                      : moment(val).format('YYYY-MM-DD')
                    handleDateChange(iso)
                  }}
                  allowClear
                  onClear={() => handleDateChange('')}
                  disableFuture
                  disablePast={false}
                  minDate={minDate}
                  maxDate={maxDate}
                  helperText={dateError || ''}
                  inputHeight={43}
                  zIndex={99999}
                  portal
                  placeholder={t('REGISTER.VISIT_DIALOG.FIRST_VISIT_DATE_PLACEHOLDER')}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '13px', color: colors.textPrimary, marginBottom: '6px' }}>
                  {t('REGISTER.VISIT_DIALOG.NOTE')}
                </Typography>
                <TextField
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                  inputProps={{ maxLength: NOTE_MAX }}
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                />
                <Typography
                  sx={{ fontSize: '12px', color: colors.disabledGray, textAlign: 'right', marginTop: '2px' }}
                >
                  {note.length}/{NOTE_MAX}
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
            <MainButton
              onClick={props.onSkip}
              title={t('REGISTER.VISIT_DIALOG.SKIP')}
              type="secondary"
              variant="outlined"
              disabled={!!props.loading}
              style={{ minWidth: 120, paddingLeft: 20, paddingRight: 20, whiteSpace: 'nowrap' }}
            />
            <MainButton
              onClick={handleConfirm}
              title={t('REGISTER.VISIT_DIALOG.CONFIRM')}
              type="primary"
              variant="contained"
              disabled={submitDisabled}
              style={{ minWidth: 120, paddingLeft: 24, paddingRight: 24, whiteSpace: 'nowrap' }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  )
}
